import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  startAfter,
  serverTimestamp,
  writeBatch,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { sanitizeProjectData } from '../utils/helpers';

export class ProjectService {

  // Ajouter projet
  static async addProject(projectData) {
    try {
      const sanitizedData = sanitizeProjectData(projectData);

      // Initialiser l'historique des statuts
      const statusHistory = [{
        status: sanitizedData.statut || 'En cours',
        date: new Date().toISOString(),
        comment: 'Création du projet'
      }];

      const docRef = await addDoc(collection(db, 'projects'), {
        ...sanitizedData,
        statusHistory,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Erreur ajout projet:', error);
      return { success: false, error: error.message };
    }
  }

  // Récupérer projets utilisateur
  static async getUserProjects(userId, lastDoc = null, limitCount = 100) {
    try {
      let q = query(
        collection(db, 'projects'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const projects = [];
      let lastDocument = null;

      querySnapshot.forEach((doc) => {
        projects.push({ id: doc.id, ...doc.data() });
        lastDocument = doc;
      });

      return {
        success: true,
        projects,
        lastDoc: lastDocument,
        hasMore: querySnapshot.size === limitCount
      };
    } catch (error) {
      console.error('Erreur récup projets:', error);
      return { success: false, error: error.message, projects: [] };
    }
  }

  // Écoute temps réel
  // options.agentResponsable : restreint aux dossiers assignés à cet agent (techniciens)
  static subscribeToUserProjects(userId, callback, options = {}) {
    try {
      const {
        limitCount = 100, orderByField = 'createdAt', orderDirection = 'desc', agentResponsable = null
      } = options;

      const constraints = [where('userId', '==', userId)];
      if (agentResponsable) {
        constraints.push(where('agentResponsable', '==', agentResponsable));
      }

      const q = query(
        collection(db, 'projects'),
        ...constraints,
        orderBy(orderByField, orderDirection),
        limit(limitCount)
      );

      return onSnapshot(q, (querySnapshot) => {
        const projects = [];
        querySnapshot.forEach((doc) => {
          projects.push({ id: doc.id, ...doc.data() });
        });
        callback(projects);
      }, (error) => {
        console.error('Erreur subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Erreur config subscription:', error);
      return () => { };
    }
  }

  // Récupérer un projet
  static async getProject(projectId) {
    try {
      const docSnap = await getDoc(doc(db, 'projects', projectId));

      if (docSnap.exists()) {
        return { success: true, project: { id: docSnap.id, ...docSnap.data() } };
      }
      return { success: false, error: 'Projet non trouvé' };
    } catch (error) {
      console.error('Erreur récup projet:', error);
      return { success: false, error: error.message };
    }
  }

  // MAJ projet
  static async updateProject(projectId, updates) {
    try {
      const sanitizedUpdates = sanitizeProjectData(updates);
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        ...sanitizedUpdates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ projet:', error);
      return { success: false, error: error.message };
    }
  }

  // Supprimer projet
  static async deleteProject(projectId) {
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      return { success: true };
    } catch (error) {
      console.error('Erreur suppr projet:', error);
      return { success: false, error: error.message };
    }
  }

  // Supprimer plusieurs projets
  static async deleteProjects(projectIds) {
    try {
      const batch = writeBatch(db);
      projectIds.forEach(id => {
        batch.delete(doc(db, 'projects', id));
      });
      await batch.commit();
      return { success: true };
    } catch (error) {
      console.error('Erreur suppr projets:', error);
      return { success: false, error: error.message };
    }
  }

  // MAJ statut avec historique
  static async updateProjectStatus(projectId, newStatus, comment = '') {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const docSnap = await getDoc(projectRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Projet non trouvé' };
      }

      const currentData = docSnap.data();
      const statusHistory = currentData.statusHistory || [];

      // Ajouter le nouveau statut à l'historique
      statusHistory.push({
        status: newStatus,
        date: new Date().toISOString(),
        previousStatus: currentData.statut,
        comment: comment
      });

      await updateDoc(projectRef, {
        statut: newStatus,
        statusHistory,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ statut:', error);
      return { success: false, error: error.message };
    }
  }

  // Ajouter avance
  static async addAvance(projectId, avanceData) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const docSnap = await getDoc(projectRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Projet non trouvé' };
      }

      const currentData = docSnap.data();
      const currentAvances = currentData.avances || [];

      const newAvance = {
        id: `avance_${Date.now()}`,
        montant: parseFloat(avanceData.montant) || 0,
        date: avanceData.date || new Date().toISOString().split('T')[0],
        description: avanceData.description || '',
        modePaiement: avanceData.modePaiement || 'espece',
        enCaisse: avanceData.enCaisse === true,
        reference: avanceData.reference || '',
        createdAt: new Date().toISOString()
      };

      await updateDoc(projectRef, {
        avances: [...currentAvances, newAvance],
        updatedAt: serverTimestamp()
      });

      return { success: true, avance: newAvance };
    } catch (error) {
      console.error('Erreur ajout avance:', error);
      return { success: false, error: error.message };
    }
  }

  // Supprimer avance
  static async deleteAvance(projectId, avanceId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const docSnap = await getDoc(projectRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Projet non trouvé' };
      }

      const currentData = docSnap.data();
      const updatedAvances = (currentData.avances || []).filter(a => a.id !== avanceId);

      await updateDoc(projectRef, {
        avances: updatedAvances,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Erreur suppr avance:', error);
      return { success: false, error: error.message };
    }
  }

  // Stats utilisateur
  static async getUserStatistics(userId) {
    try {
      const q = query(
        collection(db, 'projects'),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const projects = [];

      querySnapshot.forEach((doc) => {
        projects.push(doc.data());
      });

      const stats = {
        total: projects.length,
        statusCounts: {},
        totalRevenue: 0,
        totalPaid: 0,
        totalPending: 0,
        monthlyStats: {},
        zoneStats: {},
        communeStats: {},
        agentStats: {},
        natureStats: {}
      };

      projects.forEach(project => {
        // Comptage par statut
        const statut = project.statut || 'En cours';
        stats.statusCounts[statut] = (stats.statusCounts[statut] || 0) + 1;

        // Calculs financiers
        const montantTotal = (parseFloat(project.montantTotal) || 0) + (parseFloat(project.fraisSupplementaires) || 0);
        const totalAvances = (project.avances || []).reduce((sum, avance) =>
          sum + (parseFloat(avance.montant) || 0), 0);

        stats.totalRevenue += montantTotal;
        stats.totalPaid += totalAvances;
        stats.totalPending += (montantTotal - totalAvances);

        // Stats par mois
        if (project.date) {
          const date = new Date(project.date);
          if (!isNaN(date.getTime())) {
            const month = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            stats.monthlyStats[month] = (stats.monthlyStats[month] || 0) + 1;
          }
        }

        // Stats par zone
        if (project.zoneMerchich) {
          stats.zoneStats[project.zoneMerchich] = (stats.zoneStats[project.zoneMerchich] || 0) + 1;
        }

        // Stats par commune
        if (project.commune) {
          stats.communeStats[project.commune] = (stats.communeStats[project.commune] || 0) + 1;
        }

        // Stats par agent
        if (project.agentResponsable) {
          if (!stats.agentStats[project.agentResponsable]) {
            stats.agentStats[project.agentResponsable] = {
              count: 0,
              revenue: 0,
              completed: 0
            };
          }
          stats.agentStats[project.agentResponsable].count++;
          stats.agentStats[project.agentResponsable].revenue += montantTotal;
          if (project.statut === 'Fin Travaux') {
            stats.agentStats[project.agentResponsable].completed++;
          }
        }

        // Stats par nature
        if (project.natureProjet) {
          stats.natureStats[project.natureProjet] = (stats.natureStats[project.natureProjet] || 0) + 1;
        }
      });

      return { success: true, stats };
    } catch (error) {
      console.error('Erreur stats:', error);
      return { success: false, error: error.message };
    }
  }

  // MAJ champ unique
  static async updateProjectField(projectId, field, value) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        [field]: value,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ champ:', error);
      return { success: false, error: error.message };
    }
  }
}

// Service de gestion des agents
export class AgentService {

  // Récupérer les données utilisateur (incluant les agents)
  static async getUserData(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        return { success: true, data: docSnap.data() };
      }
      return { success: true, data: { agents: [] } };
    } catch (error) {
      console.error('Erreur récup données:', error);
      return { success: false, error: error.message };
    }
  }

  // Sauvegarder/Mettre à jour les données utilisateur
  static async saveUserData(userId, data) {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        ...data,
        updatedAt: serverTimestamp()
      }, { merge: true });

      return { success: true };
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      return { success: false, error: error.message };
    }
  }

  // Ajouter un agent
  static async addAgent(userId, agentName) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);

      let agents = [];
      if (docSnap.exists()) {
        agents = docSnap.data().agents || [];
      }

      // Vérifier si l'agent existe déjà
      if (agents.includes(agentName)) {
        return { success: false, error: 'Cet agent existe déjà' };
      }

      agents.push(agentName);

      await setDoc(userRef, {
        agents,
        updatedAt: serverTimestamp()
      }, { merge: true });

      return { success: true, agents };
    } catch (error) {
      console.error('Erreur ajout agent:', error);
      return { success: false, error: error.message };
    }
  }

  // Supprimer un agent
  static async deleteAgent(userId, agentName) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Données utilisateur non trouvées' };
      }

      const agents = (docSnap.data().agents || []).filter(a => a !== agentName);

      await setDoc(userRef, {
        agents,
        updatedAt: serverTimestamp()
      }, { merge: true });

      return { success: true, agents };
    } catch (error) {
      console.error('Erreur suppr agent:', error);
      return { success: false, error: error.message };
    }
  }

  // Récupérer la liste des agents
  static async getAgents(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        return { success: true, agents: docSnap.data().agents || [] };
      }
      return { success: true, agents: [] };
    } catch (error) {
      console.error('Erreur récup agents:', error);
      return { success: false, error: error.message, agents: [] };
    }
  }

  // Écoute temps réel des agents
  static subscribeToAgents(userId, callback) {
    try {
      const userRef = doc(db, 'users', userId);

      return onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          callback(docSnap.data().agents || []);
        } else {
          callback([]);
        }
      }, (error) => {
        console.error('Erreur subscription agents:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Erreur config subscription agents:', error);
      return () => { };
    }
  }
}

// Service de gestion des membres du cabinet (comptes agents)
export class MemberService {

  // Liste des membres rattachés à un cabinet
  static async getMembers(cabinetId) {
    try {
      const q = query(collection(db, 'users'), where('cabinetId', '==', cabinetId));
      const snap = await getDocs(q);
      const members = [];
      snap.forEach((d) => members.push({ id: d.id, ...d.data() }));
      members.sort((a, b) => (a.agentName || '').localeCompare(b.agentName || ''));
      return { success: true, members };
    } catch (error) {
      console.error('Erreur récup membres:', error);
      return { success: false, error: error.message, members: [] };
    }
  }

  // Activer / désactiver un membre (la suppression d'un compte Auth n'est pas possible côté client)
  static async setMemberActive(memberUid, isActive) {
    try {
      await updateDoc(doc(db, 'users', memberUid), {
        isActive,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ membre:', error);
      return { success: false, error: error.message };
    }
  }

  // Changer le rôle d'un membre
  static async setMemberRole(memberUid, role) {
    try {
      await updateDoc(doc(db, 'users', memberUid), {
        role,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ rôle:', error);
      return { success: false, error: error.message };
    }
  }
}
